import { useMemo } from 'react';
import useSensorDetailsInLoc from '../LocationConfig/Tagging/useSensorDetailsInLoc';
import { useAppSelector } from '../../state/store';
import { getActiveMarker, getSensorsByVarName } from '../../state/selectors';
import { SensorLatest } from '../../services/api';
import { SpaceParams } from './mapHelpers';

// processAllSpaces shows all tag spaces, else will show spaces based on active var sensors
export default function useLocAllSpaces(processAllSpaces?: boolean): SpaceParams[] {
  const sensorsDetailsInLoc = useSensorDetailsInLoc();
  const activeMarker = useAppSelector(getActiveMarker);
  const sensorsByVarName = useAppSelector(getSensorsByVarName);

  const activeVarNameSensors = useMemo(() => {
    const sensorIds = sensorsByVarName.get(activeMarker);
    return sensorsDetailsInLoc?.filter((sensor) => sensorIds?.includes(sensor.id)) ?? [];
  }, [activeMarker, sensorsByVarName, sensorsDetailsInLoc]);

  const allActiveSpaces = useMemo(
    () =>
      Array.from(
        new Set(activeVarNameSensors.flatMap((sensor: SensorLatest) => sensor?.tags ?? []))
      ),
    [activeVarNameSensors]
  );

  const activeSpaces = useMemo(() => {
    const spaceMap = new Map<string, string[]>();

    if (processAllSpaces) {
      // Process each sensor and group by tags
      sensorsDetailsInLoc.forEach((sensor: SensorLatest) => {
        // For each groupTag in the sensor, add the sensor to that space
        sensor?.tags?.forEach((tag) => {
          if (!spaceMap.has(tag)) {
            spaceMap.set(tag, []);
          }
          spaceMap.get(tag)?.push(sensor.id);
        });
      });
    } else {
      // only show spaces from active marker sensors
      allActiveSpaces.forEach((tag) => {
        const sensorsInSpace =
          sensorsDetailsInLoc
            ?.filter((sensor) => sensor?.tags?.includes(tag))
            .map((sensor) => sensor.id) ?? [];

        spaceMap.set(tag, sensorsInSpace);
      });
    }

    return Array.from(spaceMap.entries())
      .map(([title, sensors]) => ({ title, sensors }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [allActiveSpaces, processAllSpaces, sensorsDetailsInLoc]);

  return activeSpaces.length > 0 ? activeSpaces : [];
}
